import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Star, Loader2, Building2, Mail, Phone, Globe } from 'lucide-react';
import { StarRating } from './StarRating';

export const SupplierRatingDialog = ({
  open, onOpenChange,
  existingRating, selectedSupplier,
  ratingValue, setRatingValue,
  ratingHover, setRatingHover,
  reviewText, setReviewText,
  submittingRating, onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>
          {existingRating ? 'Beoordeling bewerken' : 'Leverancier beoordelen'}
        </DialogTitle>
        <DialogDescription>
          {selectedSupplier?.company_name || selectedSupplier?.name}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="text-center space-y-2">
          <p className="text-sm text-neutral-500">Hoe tevreden bent u over deze leverancier?</p>
          <div className="flex justify-center">
            <StarRating
              value={ratingValue}
              onValueChange={setRatingValue}
              hoverValue={ratingHover}
              onHoverChange={setRatingHover}
              size="lg"
            />
          </div>
          {ratingValue > 0 && (
            <p className="text-sm font-medium text-neutral-700">
              {ratingValue === 1 && 'Zeer ontevreden'}
              {ratingValue === 2 && 'Ontevreden'}
              {ratingValue === 3 && 'Neutraal'}
              {ratingValue === 4 && 'Tevreden'}
              {ratingValue === 5 && 'Zeer tevreden'}
            </p>
          )}
        </div>
        <div className="space-y-3">
          <label className="text-sm font-medium text-neutral-700">
            Toelichting (optioneel)
          </label>
          <Textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Beschrijf uw ervaring met deze leverancier..."
            className="min-h-[100px]"
            maxLength={1000}
          />
          <p className="text-xs text-neutral-400 text-right">{reviewText.length}/1000</p>
        </div>
        <div className="bg-neutral-50 rounded-lg p-3 text-sm text-neutral-600">
          <p>Uw beoordeling helpt andere klanten bij het maken van een keuze en helpt leveranciers om hun dienstverlening te verbeteren.</p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
        <Button onClick={onSubmit} disabled={ratingValue === 0 || submittingRating} data-testid="submit-rating-btn">
          {submittingRating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Star className="h-4 w-4 mr-2" />
          )}
          {existingRating ? 'Bijwerken' : 'Beoordelen'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SupplierContactDialog = ({
  open, onOpenChange,
  contactSupplier, contactMessage, setContactMessage, onSend,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md" data-testid="contact-form-dialog">
      <DialogHeader>
        <DialogTitle>Contact opnemen</DialogTitle>
        <DialogDescription>
          Stuur een bericht naar {contactSupplier?.name || 'deze leverancier'} via het Spooker platform.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-5 py-3">
        <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-medium text-black">{contactSupplier?.name}</p>
              <p className="text-xs text-neutral-500">{contactSupplier?.expertise?.join(', ') || 'Leverancier'}</p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <label className="text-sm font-medium text-black">Uw bericht</label>
          <Textarea
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
            placeholder="Beschrijf uw vraag of verzoek..."
            rows={4}
            data-testid="contact-message-input"
          />
        </div>
      </div>
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
        <Button className="bg-black text-white hover:bg-neutral-800" onClick={onSend} data-testid="send-contact-button">
          <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Verstuur Bericht
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SupplierCompareDialog = ({ open, onOpenChange, compareSelection }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="compare-dialog">
      <DialogHeader>
        <DialogTitle>Leveranciers vergelijken</DialogTitle>
        <DialogDescription>
          Vergelijk {compareSelection.length} leveranciers naast elkaar
        </DialogDescription>
      </DialogHeader>
      <div className="overflow-x-auto rounded-xl border border-neutral-200">
        <table className="spk-table" data-testid="compare-table">
          <thead>
            <tr>
              <th className="w-[140px]">Kenmerk</th>
              {compareSelection.map(s => (
                <th key={s.id} className="min-w-[180px]">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
                      <Building2 className="h-4 w-4 text-blue-600" strokeWidth={1.5} />
                    </div>
                    <span className="font-semibold text-black">{s.name || s.company_name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Status</td>
              {compareSelection.map(s => (
                <td key={s.id}>
                  <div className="flex gap-1.5 flex-wrap">
                    {s.is_marketplace && <Badge className="bg-blue-50 text-blue-700 text-[10px]">Marketplace</Badge>}
                    {(s.is_verified || s.status === 'approved') && <Badge className="bg-green-50 text-green-700 text-[10px]">Geverifieerd</Badge>}
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Beoordeling</td>
              {compareSelection.map(s => (
                <td key={s.id}>
                  {s.rating > 0 ? (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" strokeWidth={1.5} />
                      <span className="font-semibold">{s.rating.toFixed(1)}</span>
                      <span className="text-neutral-400 text-xs">/ 5</span>
                    </div>
                  ) : <span className="text-neutral-400">Geen</span>}
                </td>
              ))}
            </tr>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Expertise</td>
              {compareSelection.map(s => (
                <td key={s.id}>
                  <div className="flex flex-wrap gap-1">
                    {(s.expertise || s.services || []).map(e => (
                      <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
                    ))}
                    {(s.expertise || s.services || []).length === 0 && <span className="text-neutral-400">-</span>}
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Beschrijving</td>
              {compareSelection.map(s => (
                <td key={s.id} className="text-neutral-700 text-xs max-w-[250px]">
                  {s.description || '-'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Certificeringen</td>
              {compareSelection.map(s => (
                <td key={s.id}>
                  <div className="flex flex-wrap gap-1">
                    {(s.certifications || []).map(c => (
                      <Badge key={c} className="bg-green-50 text-green-700 text-[10px]">{c}</Badge>
                    ))}
                    {(s.certifications || []).length === 0 && <span className="text-neutral-400">-</span>}
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Locatie</td>
              {compareSelection.map(s => (
                <td key={s.id} className="text-neutral-700 text-xs">
                  {s.city || s.location || '-'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="text-xs font-medium text-neutral-500">Contact</td>
              {compareSelection.map(s => (
                <td key={s.id}>
                  <div className="space-y-2 text-xs text-neutral-700">
                    {s.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3 text-neutral-400" />{s.email}</div>}
                    {s.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3 text-neutral-400" />{s.phone}</div>}
                    {s.website && <div className="flex items-center gap-1"><Globe className="h-3 w-3 text-neutral-400" />{s.website}</div>}
                    {!s.email && !s.phone && !s.website && <span className="text-neutral-400">-</span>}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Sluiten</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
